/* The Scripts panel (Grow): what she says, section by section.

 * "Call Scripts" until 2026-09-17, when she pointed out the panel is not only
 * about calls: "call scripts should just be scripts because it can also be
 * video scripts." The rename is labels only — the id stays `salescall`, the
 * table stays `call_scripts`, and a script still carries the call TYPE it
 * always did. What a video script needs (its own sections, its own prompt) is
 * a separate decision and is NOT here yet.
 *
 * Built 2026-09-16 from docs/call-scripts-spec.md and her answers at its top.
 * Outreach's shape, because it is the same kind of thing — no Level 1 (spec
 * 8.5), so the Table and the Copy are two views of ONE list and keep the
 * toggle (rule 9b); a type asked first on Add; settings behind the cog as a
 * pop-up (rule 8); the sections are the shared Block row (block.jsx); Brill is
 * the shared Drafter (drafter.jsx), one section or the whole call.
 *
 * WHERE IT FOLLOWS CURRENT BEHAVIOUR RATHER THAN THE MOCKUPS (her answers):
 *   · Done is her mark on the SCRIPT. Sections have no Locked / Draft badge.
 *   · The Type pill is neutral, like a sequence's — colour is spent on state,
 *     not category (her call, 2026-09-15). That also settles the spec's open
 *     "Continuation colour" question without inventing one.
 *   · The rail is the shared rail, in ladder order, with the type as each row's
 *     meta. The mockup's dropdown pill is not built (answer c).
 *   · No sparkle yet — the same call Outreach made: a sparkle drafts in place
 *     from a brief, and a section has none until the call has a goal. The chat
 *     asks.
 *   · No Objection Handling and no shared sections (the go-ahead).
 */
import { useState } from "react";
import {
  SCRIPT_PRESETS, SCRIPT_STATE_LABEL, SCRIPT_TYPES, addableKinds, applyScriptSectionBlock,
  buildScriptContext, buildScriptSectionContext, groupScripts, offerMismatch, offersForType,
  parseScriptBlock, parseScriptSectionBlock, scriptLabel, scriptState, scriptTypeDef, scriptWords,
  sectionKindDef, sectionKindOf, sectionName, sectionState, sectionWords, stripCallBlock,
  SCRIPT_MEDIUMS, VIDEO_FORMS, VIDEO_PLATFORMS, VIDEO_PURPOSES, isVideo, mediumOf, platformLabels,
  videoFormDef, videoPurposeDef,
} from "../../utils/callScripts";
import { timeAgo } from "../../utils/admin";
import { buildMomentumContext } from "../../utils/momentumContext";
import { SortableItem, SortableList } from "../BrowseTable";
import { Block } from "./block";
import { Drafter } from "./drafter";
import { useRecordKeep } from "../../utils/pageChat";
import { SectionStatus } from "./fields";
import { B, STATUS_DOT, btnGhost, btnPrimary, input, nb, pDesc, pTitle } from "./tokens";
import { AddButton, Badge, Bread, ChatWithBrill, Menu2, Note, PageTitleField, SavedTick, StateDots, Status } from "./ui";
import { BarBack, L1Table, MomentumBar, RecordCopy, RecordFooter } from "./shell";
import { Settings } from "lucide-react";

/* WHAT THIS SCRIPT IS, as pills. A call: its type. A video: its form, then
   wherever it is going. Amber only when something is still to set — which for
   a video is never the platforms, because posting it in one place is normal.

   Takes the whole script, not a type: "which kind" stopped being one question
   the day the panel held videos too (2026-09-17). */
export function ScriptTypePill({ script, type }) {
  const s = script || { type };
  if (isVideo(s)) {
    const form = videoFormDef(s.form);
    const where = platformLabels(s);
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
        <Badge cls="trust" text={form ? form.label : "Video"} />
        {where.length ? <Badge cls="neutral" text={where.join(" · ")} /> : null}
      </span>
    );
  }
  const def = scriptTypeDef(s.type);
  if (!def) {
    return <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, color: B.amber, background: B.amberQuiet, border: `1px solid ${B.amberLine}`, whiteSpace: "nowrap" }}>Set type</span>;
  }
  return <span style={{ whiteSpace: "nowrap" }}><Badge cls="neutral" text={def.label} /></span>;
}

const STATE_CLS = { done: "locked", draft: "draft", empty: "retired" };
function StateTag({ state }) {
  return <Status cls={STATE_CLS[state] || "draft"} text={SCRIPT_STATE_LABEL[state] || "Draft"} />;
}


/* ── The shell ────────────────────────────────────────────────────────────────
   Add asks WHAT KIND OF CALL first (the mockup's menu), then — for a type that
   has starting calls — which one, or blank. Continuation has none yet, so it
   adds a blank script straight away. */
export function CallScriptsShell({ scripts = [], view, setView, error, onDismissError, menuOpen, setMenuOpen, onAdd, draftingBack = null, children }) {
  /* WHAT ADD ASKS, in order: call or video first (2026-09-17), then the kind of
     call (and its preset) or the FORM of the video — the one qualifier that
     changes how it is written. Platform and purpose are tags she sets behind
     the cog afterwards, because they do not change the sections. */
  /* While she is drafting, at ANY width (2026-09-24 — until then a phone
     only): the panel's chrome gives the screen up to the chat, and
     MomentumBar stands down too. The drafting screen's one row is all that
     sits above the conversation. */
  const bare = !!draftingBack;
  const [medium, setMedium] = useState(null);
  const [picked, setPicked] = useState(null);
  const close = () => { setPicked(null); setMedium(null); setMenuOpen(false); };
  const type = scriptTypeDef(picked);
  const presets = type ? SCRIPT_PRESETS.filter((p) => p.type === type.id) : [];

  return (
    <>
      {/* THE PANEL'S OWN HEAD STANDS DOWN ON A PHONE WHILE SHE DRAFTS. Her
          report, 2026-09-22: the crumb, the heading and the description were
          three of the rows that pushed the conversation down to four lines and
          the input below the fold. None of them answers a question she has
          while writing a block — the drafting screen's one row names where she
          is, and the chat card names what it is writing. */}
      {!bare && (<>
      <Bread text="Grow" />
      <h2 style={pTitle}>Scripts</h2>
      <p style={pDesc}>
        What you say, word for word, section by section. A CALL is sorted by stage — Trust Building
        calls earn the yes, Core calls are any call in your main coaching program (onboarding, check-ins,
        coaching), Continuation calls keep a client going after it. A VIDEO is hook, teaching, ask, written short or
        long for wherever it is going.
      </p>
      </>)}

      <MomentumBar view={view} setView={setView} copyLabel="Script copy" gap={20} levels={2} drafting={!!draftingBack}
        /* The way back out of the drafting screen, before Add — the drafting
           screen draws none of its own now (chromeInBar). */
        /* NOT ON THE TABLE. The drafting state lives above this and does not
           clear when she switches view, so without this she could be looking at
           the list of scripts while the bar offered her the way back out of one she
           is not on screen with. The drafting screen's own back could never do
           that — it was inside the screen. */
        lead={view !== "table" && draftingBack && <BarBack {...draftingBack} />}
        add={
        <div style={{ position: "relative", display: "inline-block" }}>
          <AddButton label="Add script" onClick={() => { setPicked(null); setMedium(null); setMenuOpen(!menuOpen); }} />
          {menuOpen && (medium === "video" ? (
            <Menu2 head="How long is it?" items={VIDEO_FORMS.map((f) => [
              f.id === "short" ? "⚡" : "🎬", f.label, f.hint, false,
              () => { close(); onAdd(null, null, { medium: "video", form: f.id }); },
            ])} />
          ) : type ? (
            <Menu2 head={`Which ${type.label.toLowerCase()} call?`} items={[
              ...presets.map((p) => ["📞", p.name, `${p.duration} · ${p.goal}`, false, () => { close(); onAdd(type.id, p.id); }]),
              ["✳️", `Your own ${type.label.toLowerCase()} call`, "Start blank and name it yourself.", false, () => { close(); onAdd(type.id, null); }],
            ]} />
          ) : medium === "call" ? (
            <Menu2 head="What kind of call is this?" items={SCRIPT_TYPES.map((t) => [
              "📞", t.label, t.hint, false,
              () => {
                if (SCRIPT_PRESETS.some((p) => p.type === t.id)) setPicked(t.id);
                else { close(); onAdd(t.id, null); }
              },
            ])} />
          ) : (
            <Menu2 head="What are you writing?" items={SCRIPT_MEDIUMS.map((m) => [
              m.id === "video" ? "🎥" : "📞", m.label, m.hint, false, () => setMedium(m.id),
            ])} />
          ))}
        </div>
      } />

      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: "pointer" }}>
          <b>Your scripts didn't save.</b> {error} Nothing was lost — try again once you're back online.
        </div>
      )}

      {children}
    </>
  );
}


/* ── Table view ───────────────────────────────────────────────────────────────
   Banded by type, in ladder order — the same banding the Offer Ladder uses. */
export const SCRIPTS_COLS = "28px 1.7fr .9fr .8fr 1.3fr 1fr .6fr .8fr .8fr 34px";

export function CallScriptsTable({ scripts = [], sectionsFor, loading, onOpen, onDelete, onReorder }) {
  return (
    <>
      <L1Table
        groups={groupScripts(scripts)} cols={SCRIPTS_COLS}
        labels={["Script", "Type", "Duration", "Goal", "Sections", "Words", "Updated", "Status", ""]}
        loading={loading} loadingText="Loading your scripts…"
        empty={<>No scripts yet. Click <b style={nb}>Add script</b>, pick what kind of call it is, and start from one of ours or your own.</>}
        onOpen={onOpen} onDelete={onDelete} deleteTitle="Delete call script"
        onReorder={onReorder}
        rowFor={(s) => {
          const sections = sectionsFor(s.id);
          const words = scriptWords(sections);
          return (
            <>
              <div style={{ minWidth: 0, fontSize: 13.5, fontWeight: 600, color: s.name ? B.text : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {scriptLabel(s)}
              </div>
              <div style={{ minWidth: 0 }}><ScriptTypePill script={s} /></div>
              <div style={{ minWidth: 0, fontSize: 12.5, color: s.duration ? B.mid : B.ghost, whiteSpace: "nowrap" }}>{s.duration || "—"}</div>
              <div style={{ minWidth: 0, fontSize: 12.5, color: s.goal ? B.mid : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.goal || "—"}</div>
              {/* A dot per section: written or not. No Done per section (answer b). */}
              <StateDots noun="section"
                states={sections.map(sectionState)}
                titles={sections.map((x) => `${sectionName(x)} — ${sectionWords(x) ? `${sectionWords(x)} words` : "not written"}`)} />
              <div style={{ fontSize: 12.5, color: words ? B.mid : B.ghost, fontVariantNumeric: "tabular-nums" }}>{words || "—"}</div>
              <div style={{ fontSize: 12, color: B.faint, whiteSpace: "nowrap" }}>{s.updatedAt ? timeAgo(s.updatedAt) : "—"}</div>
              <div><StateTag state={scriptState(s, sections)} /></div>
            </>
          );
        }} />
      {scripts.length > 0 && (
        <Note><b style={nb}>Open a call to write its script.</b> Drag by the grip to put them in your order.</Note>
      )}
    </>
  );
}


/* ── Call copy ────────────────────────────────────────────────────────────────
   The scripts down the side in ladder order, the one she is writing beside
   them. The dot marks the script Done, like a page's or a sequence's. */
export function CallScriptsCopy({ scripts = [], sectionsFor, selectedId, onSelect, onReorder, onMark, drafting, onExitToTable, children }) {
  const groups = groupScripts(scripts);
  const ordered = groups.flatMap((g) => g.rows);
  return (
    <RecordCopy
      bare={drafting}
      /* TWO LEVELS: the Table is this list, so a phone skips the list screen
         and comes straight back here. See `levels` in shell.jsx. */
      levels={2} onExit={onExitToTable} exitLabel="All scripts"
      records={ordered} selectedId={selectedId} onSelect={onSelect} onMark={onMark}
      /* The rail is one flat list in band order; a drag is turned back into a
         move inside the dragged script's band, and a drop onto another band is
         ignored rather than silently changing the script's type. */
      onReorder={(from, to) => {
        const a = ordered[from];
        const b = ordered[to];
        if (!a || !b || (a.type || null) !== (b.type || null)) return;
        const band = groups.find((g) => g.rows.includes(a));
        onReorder?.(band.key, band.rows.indexOf(a), band.rows.indexOf(b));
      }}
      noun="call script"
      emptyHint={<>No scripts yet. Click <b style={nb}>Add script</b> above to start one.</>}
      rowFor={(s) => {
        const sections = sectionsFor(s.id);
        const state = scriptState(s, sections);
        const words = scriptWords(sections);
        return {
          title: scriptLabel(s),
          meta: [scriptTypeDef(s.type)?.label || "No type yet", s.duration || null, words ? `${words} words` : null].filter(Boolean).join(" · "),
          dot: B[STATUS_DOT[state]],
          dotTitle: SCRIPT_STATE_LABEL[state],
          markable: state !== "empty",
          done: state === "done",
        };
      }}>
      {children}
    </RecordCopy>
  );
}


/* ── One call script ──────────────────────────────────────────────────────────
   Its name is the heading. Type, length, goal and offer are behind the cog and
   SHOWN in the line under the name. Done sits at the top. */
/* "call" or "video", for the sentences the screen says about a script. A video
   that keeps being called a call reads as the panel not knowing what it holds
   (2026-09-17). */
const mediumNoun = (script) => (isVideo(script) ? "video" : "call");

export function CallScript({
  script, sections = [], offers = [], error,
  onSave, onDelete, onSaveSection, onRemoveSection, onAddSection, onReorderSections,
  onDraftSection, onWrite, onSparkleSection, sparklingSectionId = null, draftedScriptSection = null,
}) {
  const [name, setName] = useState(script?.name || "");
  /* A script with no type opens with its settings up — the same rule as a
     sequence. */
  const [cog, setCog] = useState(() => !scriptTypeDef(script?.type));
  const [menuOpen, setMenuOpen] = useState(false);

  if (!script) return null;

  const state = scriptState(script, sections);
  const words = scriptWords(sections);
  const offer = offers.find((o) => o.id === script.offerId);
  const mismatch = offerMismatch(script, offers);
  const set = (k, v) => onSave({ ...script, [k]: v });
  const canAdd = addableKinds(sections, mediumOf(script));

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
        <button onClick={() => setCog(true)} title="Change what kind of call this is"
          style={{ padding: 0, border: "none", background: "none", cursor: "pointer" }}>
          <ScriptTypePill script={script} />
        </button>
        {state === "empty" ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: B.mid }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: B[STATUS_DOT[state]] }} />
            {SCRIPT_STATE_LABEL[state]}
          </span>
        ) : (
          <SectionStatus value={script.status === "done" ? "done" : "draft"} onChange={(status) => set("status", status)} />
        )}
        <span style={{ fontSize: 11.5, color: B.faint }}>{words ? `${words} words` : "No copy yet"}</span>
        <SavedTick problem={error ? "That didn't save — try again" : ""} />
        {onWrite && (
          <span style={{ marginLeft: "auto", display: "flex" }}>
            <ChatWithBrill label={`Write this ${mediumNoun(script)} with Brill`} onClick={onWrite} />
          </span>
        )}
        <button onClick={() => setCog(!cog)} title="Call script settings" aria-label="Call script settings" aria-expanded={cog}
          style={{ ...btnGhost, marginLeft: onWrite ? 0 : "auto", padding: "7px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 6, color: cog ? B.teal : B.mid, borderColor: cog ? B.tealLine : B.border2 }}>
          <Settings size={13} /> Settings
        </button>
      </div>

      <div style={{ display: "flex", marginBottom: 14 }}>
        <PageTitleField value={name} onChange={setName}
          placeholder={isVideo(script) ? "Name this video" : "Name this call"}
          label={isVideo(script) ? "Video name" : "Call script name"}
          onCommit={() => { if (name !== (script.name || "")) set("name", name); }} />
      </div>

      {cog && (
        <ScriptSettings script={script} offers={offers}
          onSave={(patch) => { onSave({ ...script, ...patch }); setCog(false); }}
          onClose={() => setCog(false)} />
      )}
      <button onClick={() => setCog(true)} title="Change these in settings"
        style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, marginBottom: 18, padding: 0, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
        <span style={{ color: script.duration ? B.mid : B.ghost }}>{script.duration || "No length set"}</span>
        <span style={{ color: B.faint }}>·</span>
        <span style={{ color: script.goal ? B.mid : B.ghost }}>{script.goal || "No goal set"}</span>
        <span style={{ color: B.faint }}>·</span>
        {/* A connected offer on the wrong rung is SAID, in amber, rather than
            quietly dropped — she chose it, and the type may be what's wrong. */}
        <span style={{ color: mismatch ? B.amber : offer ? B.mid : B.ghost }}>
          {offer ? `Connected to ${offer.name || "an untitled offer"}` : "No offer connected"}
          {mismatch ? ` — not a ${offerTierLabel(script.type)} offer` : ""}
        </span>
      </button>

      {sections.length === 0 ? (
        <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 26, textAlign: "center", color: B.dim, fontSize: 13, lineHeight: 1.6 }}>
          Nothing in this {mediumNoun(script)} yet. Click <b style={nb}>Add section</b> — {isVideo(script) ? "the hook" : "an opening"} is the usual place to start.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <SortableList ids={sections.map((s) => s.id)} onReorder={(from, to) => onReorderSections?.(script.id, from, to)}>
            {sections.map((s) => (
              <SortableItem key={s.id} id={s.id}>
                {({ handle, dragging }) => (
                  <ScriptSectionBlock
                    /* KEYED BY THE DRAFT STAMP — a Block holds its own copy of
                       the content from the moment it mounts, so without this
                       the sparkle saves the words and the row still reads
                       "Not written yet". Same as Sites, Course and Outreach. */
                    key={`${s.id}:${draftedScriptSection?.sectionId === s.id ? draftedScriptSection.n : 0}`}
                    section={s} onSave={onSaveSection} onRemove={onRemoveSection}
                    startOpen={draftedScriptSection?.sectionId === s.id}
                    onChat={onDraftSection ? () => onDraftSection(s) : null}
                    sparkle={onSparkleSection ? {
                      busy: sparklingSectionId === s.id,
                      onClick: () => onSparkleSection(s),
                      label: sectionWords(s) ? "Rewrite with Brill" : "Write with Brill",
                    } : null}
                    dragHandle={handle} dragging={dragging} />
                )}
              </SortableItem>
            ))}
          </SortableList>
        </div>
      )}
      <div style={{ position: "relative", display: "inline-block", marginTop: 12 }}>
        <button onClick={() => setMenuOpen(!menuOpen)} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>+ Add section</button>
        {menuOpen && (
          <Menu2 head={isVideo(script) ? "What does this video need?" : "What does this call need?"} items={canAdd.map((k) => [
            isVideo(script) ? "🎥" : "📞", k.label, k.hint, false, () => { setMenuOpen(false); onAddSection?.(script, k.id); },
          ])} />
        )}
      </div>

      <RecordFooter tick={false} onDelete={() => onDelete(script)} />
    </>
  );
}

function offerTierLabel(type) {
  return { trust: "Trust Building", core: "Core", continuation: "Continuation" }[scriptTypeDef(type)?.tier] || "matching";
}


/* ── Settings, as a pop-up ────────────────────────────────────────────────────
   Outreach's pattern (rule 8, her call 2026-09-16): a draft, written on Save,
   so Cancel means something. Save waits for a type. The offer list is the
   rung this type serves (answer a); an offer already connected from another
   rung stays listed, marked, so opening settings never changes it silently. */
export function ScriptSettings({ script, offers = [], onSave, onClose }) {
  const video = isVideo(script);
  const [draft, setDraft] = useState({
    type: script?.type || null, duration: script?.duration || "", goal: script?.goal || "", offerId: script?.offerId || null,
    form: script?.form || "long",
    platforms: Array.isArray(script?.platforms) ? script.platforms : [],
    purpose: script?.purpose || null,
  });
  const put = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const togglePlatform = (id) => setDraft((d) => ({
    ...d,
    platforms: d.platforms.includes(id) ? d.platforms.filter((x) => x !== id) : [...d.platforms, id],
  }));
  const def = scriptTypeDef(draft.type);
  const formDef = videoFormDef(draft.form);
  /* A video can point at any live offer — it is not a rung (utils/offerRung). */
  const allowed = offersForType(draft.type, offers, mediumOf(script));
  const current = offers.find((o) => o.id === draft.offerId);
  const stray = current && !allowed.includes(current) ? current : null;

  return (
    <div onClick={onClose} onKeyDown={(e) => { if (e.key === "Escape") onClose?.(); }}
      style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label="Call script settings" onClick={(e) => e.stopPropagation()}
        style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14, padding: "20px 20px 16px", width: "100%", maxWidth: 520, maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", boxSizing: "border-box" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 16 }}>{scriptLabel(script)}</div>
        {video ? (
          <>
            {/* FORM decides the sections and the pace, so it leads. */}
            <Setting label="How long is it" hint={formDef ? formDef.hint : "Short-form or long-form."}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {VIDEO_FORMS.map((f) => {
                  const on = draft.form === f.id;
                  return (
                    <button key={f.id} onClick={() => put("form", f.id)} aria-pressed={on} style={{ padding: "7px 13px", borderRadius: 18, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${on ? B.tealLine : B.border2}`, background: on ? B.tealQuiet : "transparent", color: on ? B.teal : B.mid, fontWeight: on ? 700 : 500 }}>
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </Setting>

            {/* PLATFORMS and PURPOSE are TAGS. Several platforms are normal, and
                each has its own rules Brill respects — her words: "different
                platforms like tiktok have specific qualifications". */}
            <Setting label="Where it goes" hint="Pick as many as you will post it to. Brill writes one script that works on all of them.">
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {VIDEO_PLATFORMS.map((pf) => {
                  const on = draft.platforms.includes(pf.id);
                  return (
                    <button key={pf.id} onClick={() => togglePlatform(pf.id)} aria-pressed={on} style={{ padding: "7px 13px", borderRadius: 18, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", border: `1px solid ${on ? B.tealLine : B.border2}`, background: on ? B.tealQuiet : "transparent", color: on ? B.teal : B.mid, fontWeight: on ? 700 : 500 }}>
                      {pf.label}
                    </button>
                  );
                })}
              </div>
            </Setting>

            <Setting label="What it is for" hint={videoPurposeDef(draft.purpose)?.hint || "Optional — it steers how Brill writes it."}>
              <select value={draft.purpose || ""} onChange={(e) => put("purpose", e.target.value || null)} aria-label="What this video is for" style={input}>
                <option value="">Not set</option>
                {VIDEO_PURPOSES.map((pp) => <option key={pp.id} value={pp.id}>{pp.label}</option>)}
              </select>
            </Setting>
          </>
        ) : (
        <Setting label="What kind of call" hint={def ? def.hint : "Pick what kind of call this is."}>
          <select autoFocus value={draft.type || ""} onChange={(e) => put("type", e.target.value || null)} aria-label="Call type" style={input}>
            {!draft.type && <option value="">Choose one…</option>}
            {SCRIPT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Setting>
        )}
        <Setting label={video ? "Run time" : "Length"}
          hint={video ? "Roughly how long the video runs — a range is fine." : "Roughly how long the call runs — a range is fine."}>
          <input value={draft.duration} onChange={(e) => put("duration", e.target.value)}
            placeholder={video ? "e.g. 6–8 min" : "e.g. 15–20 min"} aria-label={video ? "Run time" : "Length"} style={input} />
        </Setting>
        <Setting label="Goal" hint={video ? "What this video is for, in your words." : "What this call is for, in your words."}>
          <input value={draft.goal} onChange={(e) => put("goal", e.target.value)}
            placeholder={video ? "e.g. Get them to book a call" : "e.g. Screen for fit"} aria-label="Goal" style={input} />
        </Setting>
        <Setting label="Connected offer"
          hint={video
            ? "What this video points people at, if anything. Any offer — a video is not a rung."
            : def ? `${def.label} calls serve your ${offerTierLabel(draft.type)} offers.` : "Pick a type first — it decides which offers fit."}>
          <select value={draft.offerId || ""} onChange={(e) => put("offerId", e.target.value || null)} aria-label="Connected offer" style={input}>
            <option value="">No offer</option>
            {allowed.map((o) => <option key={o.id} value={o.id}>{o.name || "Untitled offer"}</option>)}
            {stray && <option value={stray.id}>{stray.name || "Untitled offer"} (not a {offerTierLabel(draft.type)} offer)</option>}
          </select>
          {!video && def && allowed.length === 0 && (
            <div style={{ fontSize: 11.5, color: B.amber, marginTop: 6, lineHeight: 1.5 }}>
              You have no {offerTierLabel(draft.type)} offers on your ladder yet.
            </div>
          )}
        </Setting>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
          <button onClick={onClose} style={{ ...btnGhost, padding: "9px 16px", fontSize: 13 }}>Cancel</button>
          <button onClick={() => onSave?.({ ...draft })} disabled={video ? !formDef : !def}
            title={video ? (formDef ? "Save" : "Pick short or long first") : (def ? "Save" : "Pick what kind of call it is first")}
            style={{ ...btnPrimary, padding: "9px 18px", fontSize: 13,
              opacity: (video ? !!formDef : !!def) ? 1 : 0.5, cursor: (video ? !!formDef : !!def) ? "pointer" : "not-allowed" }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function Setting({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: B.dim, marginTop: 6, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}


/* ── One section ──────────────────────────────────────────────────────────────
   The shared Block. Its heading is the section's name; a section of her own
   kind carries a name field above its script. The bin REMOVES the section
   (sections are hers to add and take away); the panel asks first. */
export function ScriptSectionBlock({ section, onSave, onRemove, onChat, sparkle = null, startOpen = false, dragHandle, dragging }) {
  const kind = sectionKindOf(section);
  const def = sectionKindDef(section?.sectionType);
  return (
    <Block
      kind={kind} noun="section"
      content={section?.content || {}}
      words={(c) => sectionWords({ ...section, content: c })}
      onCommit={(c) => onSave({ ...section, content: c })}
      onChat={onChat || undefined}
      /* The one-press write, the same as a website section, a course block and
         an outreach touch (her ask, 2026-09-17: "call scripts also need a
         sparkle"). The bubble opens the chat; this writes it in place. */
      sparkle={sparkle || undefined}
      /* OPEN ON WHAT BRILL JUST WROTE (her call, 2026-09-17: "it should open
         the drawer or section it just created so you can see the actual text
         right away"). Sites did this from the start; the others only
         remounted, so the words were there and out of sight. */
      startOpen={startOpen}
      onClear={() => onRemove?.(section)}
      clearLabel={`Remove ${sectionName(section).toLowerCase()}`}
      dragHandle={dragHandle} dragging={dragging}
      editTop={
        <div style={{ fontSize: 11.5, color: B.dim, lineHeight: 1.55, marginBottom: 14 }}>
          <SectionNameField section={section} onSave={onSave} placeholder={def.id === "custom" ? "Name this section" : def.label} />
          {def.hint}.
        </div>
      }
    />
  );
}

/* Her name for the section, written on the way out. Every kind can be
   renamed; an unnamed one reads as its kind. */
function SectionNameField({ section, onSave, placeholder }) {
  const [value, setValue] = useState(section?.name || "");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700 }}>Section name</span>
      <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} aria-label="Section name"
        onBlur={() => { if (value !== (section?.name || "")) onSave({ ...section, name: value }); }}
        style={{ ...input, width: "auto", flex: 1, padding: "6px 9px", fontSize: 12.5 }} />
    </div>
  );
}


/* ── Writing with Brill ───────────────────────────────────────────────────────
   COST: one API call per message — NEW SPEND with this panel. Her locked work
   rides along scoped by CONTEXT_SCOPES.call_script_copy (who she serves, her
   offer and her voice); the whole-call scope sends every section too. */
export async function scriptDraftReply({ token, script, section, sections = [], offers = [], chatContext = {}, messages, whole = false }) {
  const ctx = buildMomentumContext({
    /* A VIDEO gets its own guide (api/_lib/prompts.js video_script_copy): talking to
       a lens is not a conversation, and the hook has a second, not a minute.
       The locked context it reads is the same either way. */
    moduleId: isVideo(script) ? "video_script_copy" : "call_script_copy",
    acceleratorOutputs: chatContext.acceleratorOutputs,
    activeNiche: chatContext.activeNiche,
    isStale: (id, name) => {
      const stamp = chatContext.outputStamps?.[id]?.[name];
      return !!(chatContext.activeNiche && stamp && stamp !== chatContext.activeNiche.id);
    },
  });
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      moduleId: "call_script_copy",
      max_tokens: whole ? 3500 : 1500,
      moduleLabel: isVideo(script) ? "Video script" : "Call script",
      archetype: chatContext.userSettings?.archetype || null,
      archetypePair: chatContext.userSettings?.archetypePair || null,
      messages: [
        ...(ctx ? [{ role: "user", content: ctx }] : []),
        { role: "user", content: whole ? buildScriptContext({ script, sections, offers }) : buildScriptSectionContext({ script, section, sections, offers }) },
        ...messages,
      ],
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "Something went wrong reaching Brill — try again in a moment. Nothing in this call was touched; you can always type it yourself.");
  return data.content?.find((b) => b.type === "text")?.text || "";
}

/* Named once: the bar renders it (`draftingBack` on CallScriptsShell) and
   Next's last step reads its words off the same string. It is the only one of
   the six that is not a constant — a video whose way back says "call" is the
   panel not knowing what it holds (2026-09-17). */
export const scriptBackLabel = (script) => `← Back to the ${mediumNoun(script)}`;


export function CallScriptDrafter({ script, section, sections = [], offers = [], chatContext = {}, onSave, onBack, onGoToSection, startScope = "block", nextRecord = null }) {
  /* THE CHAT IS KEPT, per script (supabase/0049). */
  const keep = useRecordKeep({ tool: "scripts", recordId: script?.id, blockKey: section?.id, liveKeys: sections.map((s) => s.id) });
  if (!script || !section) return null;
  const kind = sectionKindOf(section);
  const has = (mod, name) => {
    const outputs = chatContext.acceleratorOutputs?.[mod] || {};
    return name ? !!outputs[name] : Object.keys(outputs).length > 0;
  };
  const contextItems = [
    { label: "Your voice", on: has("voice", "Voice Profile") },
    { label: "Your client", on: has("client", "Ideal Client Profile") },
    { label: "Your offer", on: !!offers.find((o) => o.id === script.offerId) },
    { label: "The other sections", on: sections.some((s) => s.id !== section.id && sectionWords(s) > 0) },
  ];
  const index = sections.findIndex((s) => s.id === section.id);
  const contentOf = (s) => s.content || {};

  return (
    <Drafter
      /* Recording the voice (supabase/0030): where a Lock lands. */
      voice={{ tool: isVideo(script) ? "video_script_copy" : "call_script_copy", recordType: "call_script_section", blockId: section?.id }}
      keep={keep}
      libraryKinds={["objection", "story"]}
      fieldProps={{ usedAs: { type: "script", id: script?.id, label: scriptLabel(script) } }}
      kind={kind}
      content={contentOf(section)}
      onCommit={(content) => onSave({ ...section, content })}
      ask={({ token, messages, content }) => scriptDraftReply({
        token, script, section: { ...section, content }, sections, offers, chatContext, messages,
      })}
      parse={(reply, content) => {
        const block = parseScriptSectionBlock(reply, section);
        return block ? applyScriptSectionBlock(content, block, section) : null;
      }}
      strip={stripCallBlock}
      title={`Write ${sectionName(section).toLowerCase()}`}
      opener={isVideo(script)
        ? `I'm on the ${sectionName(section).toLowerCase()} of ${scriptLabel(script)}. Tell me who this is for and what you want them to do after watching — or say "draft it" and I'll write it from your goal.`
        : `I'm on the ${sectionName(section).toLowerCase()} of ${scriptLabel(script)}. Tell me who's usually on this call and what you want them to walk away with — or say "draft it" and I'll write it from your goal.`}
      suggestions={{
        first: ["Draft it from the goal", isVideo(script) ? "I'll tell you who it's for" : "I'll tell you who's on the call"],
        later: ["Shorter", "Make it sound more like me", "More conversational"],
      }}
      contextItems={contextItems}
      pager={onGoToSection && index >= 0 ? {
        items: sections.map((s) => ({ id: s.id, label: sectionName(s), written: sectionWords(s) > 0 })),
        index,
        onGo: onGoToSection,
        noun: "Section",
      } : null}
      chromeInBar
      backLabel={scriptBackLabel(script)} onBack={onBack}
      startScope={startScope} nextRecord={nextRecord}
      record={{
        blockLabel: "Section",
        /* The scope pill and every sentence about the record read what it IS —
           a video whose chat says "call" is the panel not knowing what it
           holds (2026-09-17). */
        label: isVideo(script) ? "Video" : "Call",
        noun: mediumNoun(script),
        title: `Write ${scriptLabel(script)}`,
        heading: scriptLabel(script),
        opener: isVideo(script)
          ? `We're on the whole of ${scriptLabel(script)} — ${sections.length} section${sections.length === 1 ? "" : "s"}. Tell me who it's for and what you want them to do after watching, or say "draft it all" and I'll write every section from your goal.`
          : `We're on the whole of ${scriptLabel(script)} — ${sections.length} section${sections.length === 1 ? "" : "s"}. Tell me who's on the call and where you want it to land, or say "draft it all" and I'll write every section from your goal.`,
        suggestions: {
          first: ["Draft it all", isVideo(script) ? "I'll tell you who it's for" : "I'll tell you who's on the call"],
          later: ["Make it flow", "Shorter", "Make it sound more like me"],
        },
        blocks: sections.map((s) => ({ id: s.id, kind: sectionKindOf(s), content: contentOf(s) })),
        ask: ({ token, messages, contents }) => scriptDraftReply({
          token, script, whole: true, offers, chatContext, messages,
          sections: sections.map((s) => ({ ...s, content: contents[s.id] || contentOf(s) })),
        }),
        parse: (reply, contents) => {
          const bySection = parseScriptBlock(reply, sections);
          if (!bySection) return null;
          const out = {};
          for (const s of sections) {
            if (bySection[s.id]) out[s.id] = applyScriptSectionBlock(contents[s.id] || contentOf(s), bySection[s.id], s);
          }
          return out;
        },
        strip: stripCallBlock,
        onCommitBlock: (id, content) => {
          const s = sections.find((x) => x.id === id);
          return s ? onSave({ ...s, content }) : null;
        },
      }}
      where={scriptLabel(script)}
    />
  );
}
